import React, { useEffect, useRef, useState } from "react";
import "./MyClientForm.scss";
import { addMyClient, updateMyClient } from "../../services/api/MyClientsApi";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  getDistrictCasesByCourtRef,
  getHighCasesByCourtRef,
  getOtherCourtCasesAddedManuallyByCourtRef,
  getSupremeCourtCasesByCourtRef,
} from "../../services/api/CasesApi";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";


const initialData = {
  // salutation: "",
  name: "",
  company: "",
  phno: "",
  alternateContact: "",
  email: "",
  gstin: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  zipcode: "",
  country: "India",
  associatedCases: [],
  highCourtCases: [],
  districtCourtCases: [],
  otherCourtCases: [],
  supremeCourtCases: [],
  access: false,
  referredByName: "",
  referredByPhno: "",
};


export const MyClientForm = ({ onSuccess, onCancel }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const nameInputRef = useRef(null);
  const hasFocusedRef = useRef(false);

  // Form state
  const [formData, setFormData] = useState(initialData);
  const [isSubmitting, setIsSubmitting] = useState(false);


  const { showToast } = useToast();
  const { company } = useAuth();


  // Case selection state - adapted from ExpenseForm
  const multiDropdownRef = useRef();
  const multiInputRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedCourtTab, setSelectedCourtTab] = useState("high");
  const [searchTerm, setSearchTerm] = useState("");
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [currentCases, setCurrentCases] = useState([]);
  const [filteredCases, setFilteredCases] = useState([]);
  const [isLoadingCases, setIsLoadingCases] = useState(false);


  const getSelectedCases = () => {
    if (selectedCourtTab === "high") return formData.highCourtCases;
    if (selectedCourtTab === "district") return formData.districtCourtCases;
    if (selectedCourtTab === "supreme") return formData.supremeCourtCases || [];
    return formData.otherCourtCases || [];
  };


  // Fetch cases based on selected tab - adapted from ExpenseForm
  useEffect(() => {
    const fetchCases = async () => {
      setIsLoadingCases(true);
      try {
        const response =
          selectedCourtTab === "high"
            ? await getHighCasesByCourtRef()
            : selectedCourtTab === "district"
            ? await getDistrictCasesByCourtRef()
            : selectedCourtTab === "supreme"
            ? await getSupremeCourtCasesByCourtRef()
            : await getOtherCourtCasesAddedManuallyByCourtRef();


        setCurrentCases(response.data || []);
      } catch (err) {
        console.error("Error fetching cases:", err);
        setCurrentCases([]);
      } finally {
        setIsLoadingCases(false);
      }
    };


    fetchCases();
  }, [selectedCourtTab]);


  // Filter cases based on search term - adapted from ExpenseForm
  useEffect(() => {
    let filtered = Array.isArray(currentCases) ? currentCases : [];


    if (searchTerm.trim()) {
      filtered = filtered.filter(
        (caseItem) =>
          caseItem.parties?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          caseItem.courtRef?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          caseItem.caseDate?.caseTypeAndNumber
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
    }


    setFilteredCases(filtered);
  }, [searchTerm, currentCases, selectedCourtTab]);


  const phoneFields = ["phno", "alternateContact", "referredByPhno"];

  const handleChange = (e) => {
    const { name, value } = e.target;
    const sanitized = phoneFields.includes(name) ? value.replace(/\D/g, "") : value;
    setFormData((prev) => ({ ...prev, [name]: sanitized }));
  };


  const handleCaseToggle = (courtRef) => {
    const field =
      selectedCourtTab === "high"
        ? "highCourtCases"
        : selectedCourtTab === "district"
        ? "districtCourtCases"
        : selectedCourtTab === "supreme"
        ? "supremeCourtCases"
        : "otherCourtCases";


    setFormData((prev) => {
      const current = prev[field] || [];
      const updated = current.includes(courtRef)
        ? current.filter((ref) => ref !== courtRef)
        : [...current, courtRef];


      return { ...prev, [field]: updated };
    });
  };


  // Input handlers - adapted from ExpenseForm
  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (!isDropdownOpen) {
      setIsDropdownOpen(true);
    }
  };


  const handleInputFocus = () => {
    setIsInputFocused(true);
    setIsDropdownOpen(true);
    setSearchTerm("");
  };


  const handleInputBlur = () => {
    setTimeout(() => {
      setIsInputFocused(false);
      if (!isDropdownOpen) {
        setSearchTerm("");
      }
    }, 150);
  };


  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setIsDropdownOpen(false);
      setIsInputFocused(false);
      setSearchTerm("");
      multiInputRef.current?.blur();
    }
  };


  const handleTabChange = (tab) => {
    setSelectedCourtTab(tab);
    setSearchTerm("");
  };


  const handleCaseSelect = (caseItem) => {
    handleCaseToggle(caseItem.courtRef);
    // Don't close dropdown for multi-select, just clear search
    setSearchTerm("");
  };


  // Outside click handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        multiDropdownRef.current &&
        !multiDropdownRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
        setSearchTerm("");
      }
    };


    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    // Prevent multiple focus attempts
    if (hasFocusedRef.current) return;

    // Add a small delay to ensure DOM is ready
    const focusTimeout = setTimeout(() => {
      const nameInput = nameInputRef.current;
      if (nameInput && !hasFocusedRef.current) {
        nameInput.focus();
        
        // Move cursor to end of existing text (useful for edit mode)
        const val = nameInput.value || '';
        try {
          nameInput.setSelectionRange(val.length, val.length);
        } catch (e) {
          // Ignore selection range errors
        }
        
        hasFocusedRef.current = true;
      }
    }, 100);

    return () => clearTimeout(focusTimeout);
  }, [formData.name]);


  // Load edit data
  useEffect(() => {
    if (isEdit && location.state) {
      setFormData({ ...initialData, ...location.state });
    }
  }, [isEdit, location.state]);


  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.phno || formData.phno.trim() === "") {
      showToast("Mobile number is required", "error");
      return;
    }

    if (!formData.email || formData.email.trim() === "") {
      showToast("Email is required", "error");
      return;
    }

    // Validate phone numbers before submission
    const phoneRegex = /^\d{10}$/;

    if (!phoneRegex.test(formData.phno)) {
      showToast("Mobile number must be exactly 10 digits", "error");
      return;
    }
    if (formData.referredByPhno && !phoneRegex.test(formData.referredByPhno)) {
      showToast("Referrer's phone number must be exactly 10 digits", "error");
      return;
    }

    if (formData.alternateContact && !phoneRegex.test(formData.alternateContact)) {
      showToast("Alternate contact must be exactly 10 digits", "error");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showToast("Please enter a valid email address", "error");
      return;
    }

    setIsSubmitting(true);


    try {
      const payload = {
        ...formData,
        highCourtCases: formData.highCourtCases || [],
        districtCourtCases: formData.districtCourtCases || [],
        otherCourtCases: formData.otherCourtCases || [],
        supremeCourtCases: formData.supremeCourtCases || [],
        // Set access to false if company type is "ind", otherwise use formData.access
        access: company?.type === "ind" ? false : formData.access,
      };

      let response;
      if (isEdit) {
        response = await updateMyClient(id, payload);
      } else {
        response = await addMyClient(payload);
      }

      // Check if response contains an error code (e.g., duplicate)
      if (response.data?.code === "duplicate" || response.data?.message?.includes("already registered")) {
        showToast(response.data.message, "error");
        return;
      }

      showToast(isEdit ? "Client updated successfully!" : "Client added successfully!", "success");
      setFormData(initialData);
      if (onSuccess) return onSuccess();
      navigate("/home/my-clients");
    } catch (err) {
      console.error("Submission error:", err);
      
      // Handle specific error messages from backend
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error ||
                          "Failed to submit. Please try again.";
      
      showToast(errorMessage, "error");
    } finally {
      setIsSubmitting(false);
    }
  };


  const getDisplayValue = () => {
    if (isInputFocused) {
      return searchTerm;
    }
    return "";
  };


  const getPlaceholderText = () => {
    const totalSelected =
      (formData.highCourtCases?.length || 0) +
      (formData.districtCourtCases?.length || 0) +
      (formData.otherCourtCases?.length || 0) +
      (formData.supremeCourtCases?.length || 0);

    return totalSelected > 0
      ? `Type to search more...`
      : "Search and select cases...";
  };


  const isFormValid = () => {
    return (
      //formData.salutation.trim() !== "" &&
      formData.name.trim() !== "" &&
      formData.phno.trim() !== ""
    );
  };


  return (
    <div
      className={`client-form-container ${
        onSuccess && onCancel ? "client-form-modal-container" : ""
      }`}
    >
      <div className={`client-form-wrapper ${
        onSuccess && onCancel ? "client-form-modal-wrapper" : ""
      }`}>
        <div className="client-form-header">
          <h1 className="client-form-title">
            {isEdit ? "Update Client Details" : "Create New Client"}
          </h1>
        </div>


        <hr className="client-form-divider" />


        <form className="client-form-content" onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Basic Details</h3>
            <div className="form-row">
              {/* <div className="form-group">
                <label>Salutation <span className="clientform-required">*</span></label>
                <select
                  name="salutation"
                  value={formData.salutation}
                  onChange={handleChange}
                  required
                  ref={nameInputRef}
                >
                  <option value="">Select Salutation</option>
                  <option value="Mr.">Mr.</option>
                  <option value="Ms.">Ms.</option>
                  <option value="Mrs.">Mrs.</option>
                  <option value="Dr.">Dr.</option>
                </select>
              </div> */}
              <div className="form-group">
                <label>Name <span className="clientform-required">*</span></label>
                <input
                  
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter full name"
                  required
                />
              </div>
              <div className="form-group">
                <label>Company</label>
                <input
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Company name"
                />
              </div>
            </div>
          </div>


          <div className="form-section">
            <h3>Referred By</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Name</label>
                <input
                  name="referredByName"
                  value={formData.referredByName}
                  onChange={handleChange}
                  placeholder="Referrer's name"
                />
              </div>
              <div className="form-group">
                <label>Phone No</label>
                <input
                  name="referredByPhno"
                  value={formData.referredByPhno}
                  onChange={handleChange}
                  placeholder="9999999999"
                  inputMode="numeric"
                />
              </div>
            </div>
          </div>


          <div className="form-section">
            <h3>Associated Cases</h3>
            <div className="case-selector-container">
              <div className="case-selector-multi" ref={multiDropdownRef}>
                <label>Select Cases</label>


                <div className="dropdown-wrapper">
                  <div className="search-input-container">
                    <input
                      ref={multiInputRef}
                      type="text"
                      value={getDisplayValue()}
                      onChange={handleInputChange}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      onKeyDown={handleKeyDown}
                      className="search-input"
                      placeholder={getPlaceholderText()}
                      autoComplete="off"
                    />
                    <div className="dropdown-arrow">
                      <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                        <path
                          d="M1 1L6 6L11 1"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                  </div>


                  {/* Selected cases display */}
                  {!isInputFocused && (
                    <div className="selected-cases-container">
                      <div className="selected-cases-grid">
                        {formData.highCourtCases &&
                          formData.highCourtCases.length > 0 && (
                            <div className="court-section">
                              <div className="court-label">
                                High Court Cases:
                              </div>
                              <div className="cases-tags">
                                {formData.highCourtCases.map(
                                  (caseRef, index) => (
                                    <span
                                      key={`high-${index}`}
                                      className="case-tag high-court"
                                    >
                                      {caseRef}
                                      <button
                                        type="button"
                                        className="remove-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setFormData((prev) => ({
                                            ...prev,
                                            highCourtCases:
                                              prev.highCourtCases.filter(
                                                (ref) => ref !== caseRef
                                              ),
                                          }));
                                        }}
                                      >
                                        ×
                                      </button>
                                    </span>
                                  )
                                )}
                              </div>
                            </div>
                          )}


                        {formData.districtCourtCases &&
                          formData.districtCourtCases.length > 0 && (
                            <div className="court-section">
                              <div className="court-label">
                                District Court Cases:
                              </div>
                              <div className="cases-tags">
                                {formData.districtCourtCases.map(
                                  (caseRef, index) => (
                                    <span
                                      key={`district-${index}`}
                                      className="case-tag district-court"
                                    >
                                      {caseRef}
                                      <button
                                        type="button"
                                        className="remove-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setFormData((prev) => ({
                                            ...prev,
                                            districtCourtCases:
                                              prev.districtCourtCases.filter(
                                                (ref) => ref !== caseRef
                                              ),
                                          }));
                                        }}
                                      >
                                        ×
                                      </button>
                                    </span>
                                  )
                                )}
                              </div>
                            </div>
                          )}


                        {formData.supremeCourtCases &&
                          formData.supremeCourtCases.length > 0 && (
                            <div className="court-section">
                              <div className="court-label">
                                Supreme Court Cases:
                              </div>
                              <div className="cases-tags">
                                {formData.supremeCourtCases.map(
                                  (caseRef, index) => (
                                    <span
                                      key={`supreme-${index}`}
                                      className="case-tag supreme-court"
                                    >
                                      {caseRef}
                                      <button
                                        type="button"
                                        className="remove-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setFormData((prev) => ({
                                            ...prev,
                                            supremeCourtCases:
                                              prev.supremeCourtCases.filter(
                                                (ref) => ref !== caseRef
                                              ),
                                          }));
                                        }}
                                      >
                                        ×
                                      </button>
                                    </span>
                                  )
                                )}
                              </div>
                            </div>
                          )}

                        {formData.otherCourtCases &&
                          formData.otherCourtCases.length > 0 && (
                            <div className="court-section">
                              <div className="court-label">
                                Other Court Cases:
                              </div>
                              <div className="cases-tags">
                                {formData.otherCourtCases.map(
                                  (caseRef, index) => (
                                    <span
                                      key={`other-${index}`}
                                      className="case-tag other-court"
                                    >
                                      {caseRef}
                                      <button
                                        type="button"
                                        className="remove-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setFormData((prev) => ({
                                            ...prev,
                                            otherCourtCases:
                                              prev.otherCourtCases.filter(
                                                (ref) => ref !== caseRef
                                              ),
                                          }));
                                        }}
                                      >
                                        ×
                                      </button>
                                    </span>
                                  )
                                )}
                              </div>
                            </div>
                          )}


                        {(!formData.highCourtCases ||
                          formData.highCourtCases.length === 0) &&
                          (!formData.districtCourtCases ||
                            formData.districtCourtCases.length === 0) &&
                          (!formData.otherCourtCases ||
                            formData.otherCourtCases.length === 0) &&
                          (!formData.supremeCourtCases ||
                            formData.supremeCourtCases.length === 0) && (
                            <div className="no-selection">
                              No cases selected
                            </div>
                          )}
                      </div>
                    </div>
                  )}
                </div>


                {isDropdownOpen && (
                  <div className="dropdown-menu">
                    <div className="dropdown-header">
                      <div className="court-tabs">
                        <button
                          type="button"
                          className={`tab-btn ${
                            selectedCourtTab === "high" ? "active" : ""
                          }`}
                          onClick={() => handleTabChange("high")}
                        >
                          High Court
                        </button>
                        <button
                          type="button"
                          className={`tab-btn ${
                            selectedCourtTab === "district" ? "active" : ""
                          }`}
                          onClick={() => handleTabChange("district")}
                        >
                          District Court
                        </button>
                        <button
                          type="button"
                          className={`tab-btn ${
                            selectedCourtTab === "supreme" ? "active" : ""
                          }`}
                          onClick={() => handleTabChange("supreme")}
                        >
                          Supreme Court
                        </button>
                        <button
                          type="button"
                          className={`tab-btn ${
                            selectedCourtTab === "other" ? "active" : ""
                          }`}
                          onClick={() => handleTabChange("other")}
                        >
                          Other Court
                        </button>
                      </div>
                    </div>


                    <div className="dropdown-content">
                      {isLoadingCases ? (
                        <div className="loading-state">Loading cases...</div>
                      ) : (
                        <div className="cases-list">
                          {searchTerm &&
                            filteredCases.length === 0 &&
                            currentCases.length > 0 && (
                              <div className="no-results">
                                No cases found matching "{searchTerm}"
                              </div>
                            )}


                          {filteredCases.length === 0 && !searchTerm ? (
                            <div className="empty-state">
                              No {selectedCourtTab} court cases available
                            </div>
                          ) : (
                            filteredCases.map((caseItem, index) => {
                              const selectedCases = getSelectedCases() || [];
                              const isSelected = selectedCases.includes(
                                caseItem.courtRef
                              );


                              return (
                                <div
                                  key={`${selectedCourtTab}-${index}`}
                                  className={`case-option ${
                                    isSelected ? "selected" : ""
                                  }`}
                                  onClick={() => handleCaseSelect(caseItem)}
                                >
                                  <div className="case-details">
                                    <div className="case-parties">
                                      {caseItem.parties ||
                                        "No parties information"}
                                    </div>
                                    <div className="case-ref">
                                      Court Ref: {caseItem.courtRef}
                                    </div>
                                    {caseItem.caseDate?.caseTypeAndNumber && (
                                      <div className="case-number">
                                        Case No:{" "}
                                        {caseItem.caseDate.caseTypeAndNumber}
                                      </div>
                                    )}
                                  </div>
                                  {isSelected && (
                                    <div className="selected-icon">
                                      <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                      >
                                        <path
                                          d="M13.5 4.5L6 12L2.5 8.5"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                        />
                                      </svg>
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>


          {/* Only show Dashboard Access section if company type is NOT "ind" */}
          {company?.type !== "ind" && (
            <div className="form-section">
              <h3>Dashboard Access</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Grant Dashboard Access</label>
                  <div className="toggle-container">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        name="access"
                        checked={formData.access}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            access: e.target.checked,
                          }))
                        }
                      />
                      <span className="toggle-slider"></span>
                    </label>
                    <span className="toggle-label">
                      {formData.access ? "Access Granted" : "Access Denied"}
                    </span>
                  </div>
                  <div className="toggle-description">
                    Enable this to allow the client to access the dashboard and
                    view case updates.
                  </div>
                </div>
              </div>
            </div>
          )}


          <div className="form-section">
            <h3>Contact Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Mobile Number <span className="clientform-required">*</span></label>
                <input
                  name="phno"
                  value={formData.phno}
                  onChange={handleChange}
                  placeholder="9999999999"
                  inputMode="numeric"
                  required
                />
              </div>
              <div className="form-group">
                <label>Email <span className="clientform-required">*</span></label>
                <input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="example@email.com"
                />
              </div>
            </div>


            <div className="form-row">
              <div className="form-group">
                <label>Alternate Contact</label>
                <input
                  name="alternateContact"
                  value={formData.alternateContact}
                  onChange={handleChange}
                  placeholder="9999999999"
                  inputMode="numeric"
                />
              </div>
              <div className="form-group">
                <label>GSTIN</label>
                <input
                  name="gstin"
                  value={formData.gstin}
                  onChange={handleChange}
                  placeholder="Enter GSTIN number"
                />
              </div>
            </div>
          </div>


          <div className="form-section end-client">
            <h3>Contact Address</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Address Line 1</label>
                <input
                  name="addressLine1"
                  value={formData.addressLine1}
                  onChange={handleChange}
                  placeholder="Street address"
                />
              </div>
              <div className="form-group">
                <label>Address Line 2</label>
                <input
                  name="addressLine2"
                  value={formData.addressLine2}
                  onChange={handleChange}
                  placeholder="Apartment, suite, etc."
                />
              </div>
            </div>


            <div className="form-row">
              <div className="form-group">
                <label>City</label>
                <input
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="City"
                />
              </div>
              <div className="form-group">
                <label>State</label>
                <input
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  placeholder="State"
                />
              </div>
            </div>


            <div className="form-row">
              <div className="form-group">
                <label>Zipcode</label>
                <input
                  name="zipcode"
                  value={formData.zipcode}
                  onChange={handleChange}
                  placeholder="Postal code"
                />
              </div>
              <div className="form-group">
                <label>Country</label>
                <input
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  placeholder="Country"
                />
              </div>
            </div>
          </div>


          <div className="form-actions">
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting || !isFormValid()}
            >
              {isSubmitting
                ? isEdit
                  ? "Updating..."
                  : "Adding..."
                : isEdit
                ? "Update Client"
                : "Add Client"}
            </button>
            <button
              type="button"
              className="btn-cancel"
              onClick={() => {
                if (onCancel) return onCancel();
                navigate("/home/my-clients");
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

